import { assign } from "@suse-o11y/assign";

import { ToggleList as ToggleListComponent } from "./ToggleList";
import { ToggleListLocationContent, ToggleListLocationTrigger } from "./ToggleListLocation";
import { ToggleListRoot } from "./ToggleListRoot";

export { ToggleListStateProvider } from "./ToggleListStateProvider";

/**
 * Depending on the location and size of `each` array,
 * it renders either the ToggleListButton or the uniq sorted `each -> children`.
 *
 * @example
 *
 * // The Property component is a host for the ToggleList.
 * // If `children` include ToggleList with > 1 item than ToggleList.Content renders the list
 * // and ToggleList.Trigger renders a button that toggles the visibility of the ToggleList.Content,
 * // otherwise, it renders the `children` directly inside the ToggleList.Trigger but ToggleList.Content does not render anything.
 * const Property: React.FC<{
 *   label: string;
 * }> = ({ label, children }) => {
 *   const viewId = useViewId();
 *
 *   return (
 *     <ToggleList.Root namespace={{ viewId, label }}>
 *       <div>
 *         <h6>{label}</h6>
 *
 *         <ToggleList.Trigger>{children}</ToggleList.Trigger>
 *       </div>
 *
 *       <ToggleList.Content>{children}</ToggleList.Content>
 *     </ToggleList.Root>
 *   );
 * };
 *
 * // The AboutProperties component uses the Property component,
 * // and where it makes sense, it wraps the `children` with the ToggleList.
 * // When it does, the Property component renders the toggle button if necessary and the list of tags when expanded.
 * const AboutProperties: React.FC<{
 *   component: Component;
 * }> = ({ component }) => (
 *   <div>
 *     <Property label="name">{component.name}</Property>
 *     <Property label="description">{component.description}</Property>
 *     <Property label="tags">
 *       <ToggleList namespace={["AboutProperties", component.id]} each={component.tags}>
 *         {(tag) => <Tag text={tag} />}
 *       </ToggleList>
 *     </Property>
 *   </div>
 * );
 */
export const ToggleList = assign(ToggleListComponent, {
	/**
	 * Provides the composite namespace for the ToggleList,
	 * so it can be later on used to get/set the state of the ToggleList.
	 */
	Root: ToggleListRoot,

	/**
	 * Defines the 'trigger' location for the ToggleList.
	 * If it takes ToggleList among its children,
	 * it renders either the ToggleListButton or the `children` directly.
	 */
	Trigger: ToggleListLocationTrigger,

	/**
	 * Defines the 'content' location for the ToggleList.
	 * If the ToggleList is not expanded, the content is not rendered.
	 * It should take ToggleList among its children to render them when the ToggleList is expanded.
	 */
	Content: ToggleListLocationContent,
});
